import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChessBoardView from '@/components/chess/ChessBoardView';
import PGNExerciseEditor from '@/components/database/PGNExerciseEditor';
import { toast } from 'sonner';
import {
  Upload, Search, FolderPlus, Folder, MoreVertical, Trash2, Lock, Pencil,
  SkipBack, SkipForward, ChevronLeft, ChevronRight, FlipHorizontal2, Filter, X, Save,
  ChevronDown, ChevronRightIcon, FolderInput
} from 'lucide-react';

function pill(v) { return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-mono">{v}</span>; }

function displayPlayerName(value, fallback) {
  const name = String(value ?? '').trim();
  return !name || ['?', 'unknown', '-'].includes(name.toLowerCase())
    ? fallback
    : name;
}

function displayMetadata(value) {
  const text = String(value ?? '').trim();
  return !text || ['?', 'unknown', '-'].includes(text.toLowerCase())
    ? '—'
    : text;
}
const RESULTS = [
  { v: '',        label: 'Todos' },
  { v: '1-0',     label: '1-0' },
  { v: '0-1',     label: '0-1' },
  { v: '1/2-1/2', label: '½-½' },
  { v: '*',       label: '*' },
];
const NO_MOD = '__none__';

export default function DatabasePage() {
  // The Base de datos maestra is organized as ONE root ("My Databases")
  // containing 10 module slots (0..9). Shared/Public trees are hidden per
  // spec — they still exist in the API, we simply don't render them here.
  const [dbs, setDbs] = useState([]);
  const [labels, setLabels] = useState(Array.from({length: 10}, (_, i) => `Módulo ${i+1}`));
  const [selectedDb, setSelectedDb] = useState(null);
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedGameDetail, setSelectedGameDetail] = useState(null);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [deletingGames, setDeletingGames] = useState(false);
  const [orientation, setOrientation] = useState('white');
  const [ply, setPly] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);

  // Root + each module slot has its own open/closed state.
  const [openRoot, setOpenRoot] = useState(true);
  const [openMods, setOpenMods] = useState(() => {
    const s = {}; for (let i = 0; i < 10; i++) s[i] = false; s[NO_MOD] = true; return s;
  });

  // filters
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({
    white: '', black: '', result: '', eco: '', opening: '', event: '',
    elo_min: '', elo_max: '', date_from: '', date_to: '', min_moves: '', max_moves: '',
  });
  const [openFilters, setOpenFilters] = useState(false);

  // upload / create
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', module_slot: NO_MOD });
  const [openUpload, setOpenUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  // rename module dialog
  const [renameSlot, setRenameSlot] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadDbs = useCallback(async () => {
    try {
      const [{ data: d }, { data: L }] = await Promise.all([
        api.get('/pgn-databases'),
        api.get('/pgn-module-labels'),
      ]);
      const list = d.mine || [];
      setDbs(list);
      setLabels(L.labels || labels);
      if (!selectedDb && list[0]) setSelectedDb(list[0]);
    } catch { /* silent */ }
  }, [selectedDb]); // eslint-disable-line

  useEffect(() => { loadDbs(); }, [loadDbs]);

  const loadGames = useCallback(async () => {
    if (!selectedDb) { setGames([]); setTotal(0); return; }
    const params = { q: q || undefined, ...filters, skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    Object.keys(params).forEach(k => { if (!params[k] && params[k] !== 0) delete params[k]; });
    const { data } = await api.get(`/pgn-databases/${selectedDb.id}/games`, { params });
    const items = data.items || [];
    setGames(items);
    setTotal(data.total ?? items.length);
    if (items[0]) selectGame(items[0]);
    else { setSelectedGame(null); setSelectedGameDetail(null); }
  }, [selectedDb, q, filters, page]); // eslint-disable-line

  useEffect(() => { loadGames(); }, [loadGames]);
  useEffect(() => { setPage(0); }, [selectedDb, q, filters]);
  useEffect(() => { setSelectedGameIds([]); }, [selectedDb?.id]);

  const toggleVisibleGames = (checked) => {
    const visibleIds = games.map(row => row.id);
    setSelectedGameIds(current => {
      if (checked) return [...new Set([...current, ...visibleIds])];
      return current.filter(id => !visibleIds.includes(id));
    });
  };

  const deleteSelectedGames = async () => {
    if (!selectedDb || selectedGameIds.length === 0 || deletingGames) return;
    const count = selectedGameIds.length;
    if (!window.confirm(
      `¿Eliminar ${count} partida${count === 1 ? '' : 's'} de "${selectedDb.name}"? Esta acción no se puede deshacer.`
    )) return;

    setDeletingGames(true);
    try {
      const { data } = await api.post(
        `/pgn-databases/${selectedDb.id}/games/delete`,
        { game_ids: selectedGameIds }
      );
      setSelectedGameIds([]);
      toast.success(`Partidas eliminadas: ${data.deleted}`);
      await loadDbs();
      await loadGames();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar las partidas');
    } finally {
      setDeletingGames(false);
    }
  };

  const selectGameSeqRef = useRef(0);
  const selectGame = async (g) => {
    setSelectedGame(g);
    // Fix carrera: si el usuario cambia rápido entre partidas A y B, la
    // respuesta tardía de A NO debe sobrescribir el detalle actual (B).
    // Cada llamada incrementa la secuencia; solo la más reciente aplica.
    const mySeq = ++selectGameSeqRef.current;
    try {
      const { data } = await api.get(`/games/${g.id}`);
      if (mySeq !== selectGameSeqRef.current) return;
      setSelectedGameDetail(data);
      setPly((data.moves || []).length);
    } catch {
      if (mySeq !== selectGameSeqRef.current) return;
      setSelectedGameDetail(null);
    }
  };

  const positions = useMemo(() => {
    if (!selectedGameDetail) return [];
    const c = new Chess(selectedGameDetail.starting_fen || undefined);
    const fens = [c.fen()];
    for (const m of (selectedGameDetail.moves || [])) {
      if (m.fen) { fens.push(m.fen); try { c.load(m.fen); } catch {} continue; }
      try { c.move(m.san); fens.push(c.fen()); } catch { break; }
    }
    return fens;
  }, [selectedGameDetail]);
  const boardFen = useMemo(() => {
    if (!positions.length) return new Chess().fen();
    return positions[Math.min(Math.max(0, ply), positions.length - 1)];
  }, [positions, ply]);

  const grouped = useMemo(() => {
    const g = { unassigned: [] };
    for (let i = 0; i < 10; i++) g[i] = [];
    for (const d of dbs) {
      const s = typeof d.module_slot === 'number' && d.module_slot >= 0 && d.module_slot <= 9 ? d.module_slot : null;
      if (s === null) g.unassigned.push(d);
      else g[s].push(d);
    }
    return g;
  }, [dbs]);

  const createDb = async () => {
    if (!createForm.name.trim()) { toast.error('Nombre requerido'); return; }
    try {
      const { data } = await api.post('/pgn-databases', {
        name: createForm.name, description: createForm.description, visibility: 'private',
      });
      // If the user picked a module during create, assign it via PATCH.
      if (createForm.module_slot !== NO_MOD) {
        try { await api.patch(`/pgn-databases/${data.id}`, { module_slot: Number(createForm.module_slot) }); } catch {}
      }
      toast.success('Base creada');
      setOpenCreate(false);
      setCreateForm({ name: '', description: '', module_slot: NO_MOD });
      setSelectedDb(data);
      loadDbs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const removeDb = async (d) => {
    if (!window.confirm(`¿Borrar la base "${d.name}" y todas sus partidas?`)) return;
    try {
      await api.delete(`/pgn-databases/${d.id}`);
      toast.success('Base borrada');
      if (selectedDb?.id === d.id) setSelectedDb(null);
      loadDbs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const moveDbToSlot = async (d, slot) => {
    // slot: 0..9 or null (unassign)
    try {
      await api.patch(`/pgn-databases/${d.id}`, { module_slot: slot });
      toast.success(slot === null ? 'Base movida a Sin módulo' : `Base movida a ${labels[slot]}`);
      loadDbs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openRename = (slot) => { setRenameSlot(slot); setRenameValue(labels[slot] || ''); };
  const submitRename = async () => {
    const v = (renameValue || '').trim();
    if (!v) { toast.error('Nombre requerido'); return; }
    const next = [...labels]; next[renameSlot] = v;
    try {
      const { data } = await api.put('/pgn-module-labels', { labels: next });
      setLabels(data.labels || next);
      toast.success('Módulo renombrado');
      setRenameSlot(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const doUpload = async () => {
    if (!selectedDb) { toast.error('Selecciona una base primero'); return; }
    if (!uploadFile) { toast.error('Selecciona un archivo .pgn'); return; }
    setUploading(true); setUploadReport(null);
    const fd = new FormData(); fd.append('file', uploadFile);
    try {
      const { data } = await api.post(`/pgn-databases/${selectedDb.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      setUploadReport(data);
      toast.success(`Importadas ${data.imported}/${data.total} · duplicadas ${data.duplicates} · errores ${data.errors_count}`);
      loadDbs(); loadGames();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al importar'); }
    finally { setUploading(false); }
  };

  const clearFilters = () => setFilters({
    white:'',black:'',result:'',eco:'',opening:'',event:'',
    elo_min:'',elo_max:'',date_from:'',date_to:'',min_moves:'',max_moves:''
  });

  const g = selectedGame;
  const totalMoves = (selectedGameDetail?.moves || []).length;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Base de datos</h1>
          <p className="text-xs text-slate-500">Almacén maestro organizado por módulos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="new-db-btn" variant="outline" onClick={()=>setOpenCreate(true)} className="rounded-lg">
            <FolderPlus className="w-4 h-4 mr-1"/> Nueva base
          </Button>
          <Button data-testid="upload-pgn-btn" onClick={()=>setOpenUpload(true)}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-6">
            <Upload className="w-4 h-4 mr-1"/> Upload PGN
          </Button>
        </div>
      </div>

      {/* 3-col layout */}
      <div className="flex-1 grid grid-cols-[280px_1fr_420px] min-h-0">
        {/* LEFT: search + modules tree */}
        <div className="border-r border-slate-200 p-4 overflow-y-auto">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input data-testid="db-search" placeholder="Search…" value={q}
              onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-lg"/>
          </div>

          {/* My Databases root */}
          <div data-testid="tree-mine" className="mb-1">
            <button onClick={()=>setOpenRoot(o=>!o)} className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded text-sm font-semibold">
              {openRoot ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRightIcon className="w-3.5 h-3.5"/>}
              <Folder className="w-4 h-4 text-slate-500"/> My Databases
              <span className="ml-auto text-xs text-slate-400 font-mono">{dbs.length}</span>
            </button>
            {openRoot && (
              <div className="ml-3 mt-1 space-y-0.5">
                {Array.from({length: 10}, (_, i) => i).map(slot => (
                  <ModuleNode key={slot} slot={slot} label={labels[slot]}
                    items={grouped[slot]}
                    open={!!openMods[slot]}
                    onToggle={()=>setOpenMods(s=>({...s,[slot]:!s[slot]}))}
                    onRename={()=>openRename(slot)}
                    onSelect={setSelectedDb}
                    selectedId={selectedDb?.id}
                    onDelete={removeDb}
                    onMove={moveDbToSlot}
                    labels={labels}/>
                ))}
                {/* Sin módulo (fallback for databases without slot) */}
                {grouped.unassigned.length > 0 && (
                  <ModuleNode slot={NO_MOD} label="Sin módulo"
                    items={grouped.unassigned}
                    open={!!openMods[NO_MOD]}
                    onToggle={()=>setOpenMods(s=>({...s,[NO_MOD]:!s[NO_MOD]}))}
                    onSelect={setSelectedDb}
                    selectedId={selectedDb?.id}
                    onDelete={removeDb}
                    onMove={moveDbToSlot}
                    labels={labels}
                    unassigned/>
                )}
              </div>
            )}
          </div>
        </div>

        {/* MIDDLE: games table */}
        <div className="p-4 overflow-y-auto">
          {selectedDb ? (
            <>
              <div className="flex flex-wrap items-baseline gap-6 mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Base</div>
                  <div className="font-semibold text-sm">{selectedDb.name}</div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span data-testid="games-total" className="text-xs text-slate-500 font-mono">
                    {total} partida{total===1?'':'s'}
                  </span>
                  {selectedGameIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={deletingGames}
                      onClick={deleteSelectedGames}
                      data-testid="delete-selected-games"
                      className="rounded-lg text-rose-600"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1"/>
                      {deletingGames ? 'Eliminando...' : `Borrar (${selectedGameIds.length})`}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={()=>setOpenFilters(o=>!o)}
                    data-testid="toggle-filters" className="rounded-lg">
                    <Filter className="w-3.5 h-3.5 mr-1"/> Filtros
                  </Button>
                  {(Object.values(filters).some(Boolean) || q) && (
                    <Button variant="ghost" size="sm" onClick={()=>{clearFilters(); setQ('');}} className="rounded-lg">
                      <X className="w-3.5 h-3.5 mr-1"/> Limpiar
                    </Button>
                  )}
                </div>
              </div>

              {openFilters && (
                <div className="mb-4 rounded-xl bg-slate-50 border border-slate-200 p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input data-testid="f-white" placeholder="Blancas" value={filters.white} onChange={e=>setFilters(s=>({...s,white:e.target.value}))}/>
                  <Input data-testid="f-black" placeholder="Negras" value={filters.black} onChange={e=>setFilters(s=>({...s,black:e.target.value}))}/>
                  <Select value={filters.result} onValueChange={v=>setFilters(s=>({...s,result:v==='__all__'?'':v}))}>
                    <SelectTrigger data-testid="f-result"><SelectValue placeholder="Resultado"/></SelectTrigger>
                    <SelectContent>
                      {RESULTS.map(r => <SelectItem key={r.v||'__all__'} value={r.v||'__all__'}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid="f-eco" placeholder="ECO" value={filters.eco} onChange={e=>setFilters(s=>({...s,eco:e.target.value}))}/>
                  <Input data-testid="f-opening" placeholder="Apertura" value={filters.opening} onChange={e=>setFilters(s=>({...s,opening:e.target.value}))}/>
                  <Input data-testid="f-event" placeholder="Evento" value={filters.event} onChange={e=>setFilters(s=>({...s,event:e.target.value}))}/>
                  <Input data-testid="f-elo-min" placeholder="Elo mín" type="number" value={filters.elo_min} onChange={e=>setFilters(s=>({...s,elo_min:e.target.value}))}/>
                  <Input data-testid="f-elo-max" placeholder="Elo máx" type="number" value={filters.elo_max} onChange={e=>setFilters(s=>({...s,elo_max:e.target.value}))}/>
                  <Input data-testid="f-date-from" placeholder="Fecha desde (YYYY.MM.DD)" value={filters.date_from} onChange={e=>setFilters(s=>({...s,date_from:e.target.value}))}/>
                  <Input data-testid="f-date-to" placeholder="Fecha hasta (YYYY.MM.DD)" value={filters.date_to} onChange={e=>setFilters(s=>({...s,date_to:e.target.value}))}/>
                  <Input data-testid="f-min-moves" placeholder="Min jugadas" type="number" value={filters.min_moves} onChange={e=>setFilters(s=>({...s,min_moves:e.target.value}))}/>
                  <Input data-testid="f-max-moves" placeholder="Max jugadas" type="number" value={filters.max_moves} onChange={e=>setFilters(s=>({...s,max_moves:e.target.value}))}/>
                </div>
              )}

              {games.length === 0 ? (
                <EmptyState/>
              ) : (
                <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-bold">
                      <tr>
                        <th className="p-3 text-center w-10">
                          <input
                            type="checkbox"
                            aria-label="Seleccionar todas las partidas visibles"
                            checked={games.length > 0 && games.every(row => selectedGameIds.includes(row.id))}
                            onChange={e => toggleVisibleGames(e.target.checked)}
                            onClick={e => e.stopPropagation()}
                          />
                        </th>
                        <th className="p-3 text-left w-14">S No</th>
                        <th className="p-3 text-left">Blancas</th>
                        <th className="p-3 text-left">Negras</th>
                        <th className="p-3 text-left w-24">Resultado</th>
                        <th className="p-3 text-left">Apertura</th>
                        <th className="p-3 text-right w-20">Jugadas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map((row, i) => (
                        <tr key={row.id}
                          data-testid={`game-row-${row.id}`}
                          onClick={()=>selectGame(row)}
                          className={`border-t hover:bg-slate-50 cursor-pointer ${selectedGame?.id===row.id?'bg-indigo-50':''}`}>
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar partida ${i+1}`}
                              checked={selectedGameIds.includes(row.id)}
                              onChange={e => {
                                setSelectedGameIds(current =>
                                  e.target.checked
                                    ? [...new Set([...current, row.id])]
                                    : current.filter(id => id !== row.id)
                                );
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td className="p-3 font-mono text-slate-500">{i+1}</td>
                          <td className="p-3 font-semibold">{displayPlayerName(row.white_player, 'Blancas')}
                            {row.white_elo ? <span className="text-slate-400 text-xs ml-1">({row.white_elo})</span> : null}</td>
                          <td className="p-3 font-semibold">{displayPlayerName(row.black_player, 'Negras')}
                            {row.black_elo ? <span className="text-slate-400 text-xs ml-1">({row.black_elo})</span> : null}</td>
                          <td className="p-3">{pill(row.result || '*')}</td>
                          <td className="p-3 text-xs text-slate-600">{displayMetadata(row.eco)} · {displayMetadata(row.opening)}</td>
                          <td className="p-3 text-right font-mono text-slate-500">{row.num_moves ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {total > PAGE_SIZE && (
                <div className="mt-3 flex items-center justify-center gap-2" data-testid="db-pagination">
                  <Button size="sm" variant="outline" data-testid="page-prev" disabled={page===0}
                    onClick={()=>setPage(p=>Math.max(0,p-1))}><ChevronLeft className="w-4 h-4"/></Button>
                  <span className="text-xs font-mono text-slate-500">
                    Página {page+1} / {Math.ceil(total/PAGE_SIZE)}
                  </span>
                  <Button size="sm" variant="outline" data-testid="page-next" disabled={(page+1)*PAGE_SIZE>=total}
                    onClick={()=>setPage(p=>p+1)}><ChevronRight className="w-4 h-4"/></Button>
                </div>
              )}
            </>
          ) : (
            <div className="grid place-items-center h-full text-sm text-slate-500">
              Crea o selecciona una base para empezar
            </div>
          )}
        </div>

        {/* RIGHT: board + player info */}
        <div className="border-l border-slate-200 p-4 flex flex-col items-center overflow-y-auto">
          <div className="text-center mb-3">
            <div className="text-sm font-semibold">{displayPlayerName(g?.white_player, 'Blancas')} <span className="text-slate-400">vs</span> {displayPlayerName(g?.black_player, 'Negras')}</div>
            <div className="text-xs text-slate-500">{displayMetadata(g?.event)} · {displayMetadata(g?.date)}</div>
            <div className="text-xs text-slate-500">ECO {displayMetadata(g?.eco)} · {displayMetadata(g?.opening)}</div>
          </div>
          <ChessBoardView fen={boardFen} boardWidth={360} orientation={orientation} arePiecesDraggable={false}/>
          <div className="mt-3 flex items-center gap-1">
            <Button size="icon" variant="outline" data-testid="rp-first" onClick={()=>setPly(0)} disabled={!totalMoves}><SkipBack className="w-4 h-4"/></Button>
            <Button size="icon" variant="outline" data-testid="rp-prev" onClick={()=>setPly(p=>Math.max(0,p-1))} disabled={!totalMoves}><ChevronLeft className="w-4 h-4"/></Button>
            <Button size="icon" variant="outline" data-testid="rp-flip" onClick={()=>setOrientation(o=>o==='white'?'black':'white')}><FlipHorizontal2 className="w-4 h-4"/></Button>
            <Button size="icon" variant="outline" data-testid="rp-next" onClick={()=>setPly(p=>Math.min(totalMoves,p+1))} disabled={!totalMoves}><ChevronRight className="w-4 h-4"/></Button>
            <Button size="icon" variant="outline" data-testid="rp-last" onClick={()=>setPly(totalMoves)} disabled={!totalMoves}><SkipForward className="w-4 h-4"/></Button>
          </div>
          <div className="mt-2 text-xs font-mono text-slate-500">Jugada {ply}/{totalMoves}</div>
          {selectedGameDetail && (
            <div className="mt-3 w-full max-w-full">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Movimientos</div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 max-h-40 overflow-y-auto font-mono text-xs leading-relaxed">
                {(selectedGameDetail.moves || []).map((m,i)=>(
                  <span key={i} onClick={()=>setPly(i+1)}
                    className={`cursor-pointer px-1 rounded ${(i+1)===ply?'bg-amber-200':'hover:bg-slate-200'}`}>
                    {i%2===0 ? `${Math.floor(i/2)+1}.` : ''}{m.san}{' '}
                  </span>
                ))}
              </div>
              <Button
                data-testid="edit-exercise-btn"
                size="sm"
                className="mt-2 w-full rounded-lg"
                disabled={!selectedDb || !selectedGameDetail}
                onClick={() => setEditorOpen(true)}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar ejercicio y variantes
              </Button>
              <Button data-testid="save-position-btn" size="sm" variant="outline" className="mt-2 w-full rounded-lg"
                onClick={()=>{
                  navigator.clipboard.writeText(boardFen);
                  toast.success('FEN copiado — úsalo para crear un ejercicio en Ejercicios');
                }}>
                <Save className="w-3.5 h-3.5 mr-1"/> Guardar posición como ejercicio
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editor de ejercicios PGN</DialogTitle>
            <DialogDescription>
              Añade jugadas y variantes a la partida seleccionada. Los cambios se guardan en esa misma partida.
            </DialogDescription>
          </DialogHeader>
          {selectedGameDetail && (
            <PGNExerciseEditor
              databaseId={selectedDb?.id}
              game={selectedGameDetail}
              onClose={() => setEditorOpen(false)}
              onSaved={async () => {
                const id = selectedGameDetail.id;
                const { data } = await api.get(`/games/${id}`);
                setSelectedGameDetail(data);
                setSelectedGame(data);
                setGames(current => current.map(row => row.id === id ? { ...row, ...data } : row));
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create DB dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent data-testid="create-db-dialog">
          <DialogHeader><DialogTitle>Nueva base de datos</DialogTitle>
            <DialogDescription>Crea una carpeta para organizar partidas de ajedrez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label>
              <Input data-testid="create-db-name" value={createForm.name} onChange={e=>setCreateForm(f=>({...f,name:e.target.value}))} placeholder="Ej: Aperturas Módulo 1"/></div>
            <div><Label>Descripción</Label>
              <Input data-testid="create-db-desc" value={createForm.description} onChange={e=>setCreateForm(f=>({...f,description:e.target.value}))}/></div>
            <div><Label>Módulo</Label>
              <Select value={String(createForm.module_slot)} onValueChange={v=>setCreateForm(f=>({...f,module_slot: v === NO_MOD ? NO_MOD : Number(v)}))}>
                <SelectTrigger data-testid="create-db-module"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_MOD}>Sin módulo</SelectItem>
                  {labels.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="create-db-submit" onClick={createDb} className="rounded-lg bg-indigo-600 hover:bg-indigo-700">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename module dialog */}
      <Dialog open={renameSlot !== null} onOpenChange={(v)=>{ if(!v) setRenameSlot(null); }}>
        <DialogContent data-testid="rename-module-dialog">
          <DialogHeader>
            <DialogTitle>Renombrar módulo</DialogTitle>
            <DialogDescription>Cambia el nombre visible del módulo en tu Base de datos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Nombre</Label>
            <Input data-testid="rename-module-input" value={renameValue} onChange={e=>setRenameValue(e.target.value)}/>
          </div>
          <DialogFooter>
            <Button data-testid="rename-module-submit" onClick={submitRename} className="rounded-lg bg-indigo-600 hover:bg-indigo-700">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload PGN dialog */}
      <Dialog open={openUpload} onOpenChange={(v)=>{setOpenUpload(v); if(!v){ setUploadFile(null); setUploadReport(null);} }}>
        <DialogContent data-testid="upload-pgn-dialog">
          <DialogHeader><DialogTitle>Cargar PGN</DialogTitle>
            <DialogDescription>Sube un archivo .pgn con una o miles de partidas. Las duplicadas se omiten.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Base de destino</Label>
              <div className="text-sm font-semibold">{selectedDb ? selectedDb.name : <span className="text-rose-500">Selecciona una base primero</span>}</div>
            </div>
            <div>
              <Label>Archivo .pgn</Label>
              <input type="file" accept=".pgn,text/plain" data-testid="pgn-file"
                onChange={(e)=>{ setUploadFile(e.target.files?.[0] || null); setUploadReport(null); }}
                className="block text-sm mt-1"/>
              <p className="text-xs text-slate-500 mt-1">Puede contener una o miles de partidas. Se ignoran duplicadas.</p>
            </div>
            {uploadReport && (
              <div className="rounded-lg bg-slate-50 border p-3 text-sm space-y-1">
                <div className="font-semibold">Reporte de importación</div>
                <div>Archivo: <b>{uploadReport.file}</b></div>
                <div>Partidas encontradas: <b>{uploadReport.total}</b></div>
                <div>Importadas: <b className="text-emerald-600">{uploadReport.imported}</b></div>
                <div>Duplicadas (omitidas): <b className="text-amber-600">{uploadReport.duplicates}</b></div>
                <div>Errores: <b className="text-rose-600">{uploadReport.errors_count}</b></div>
                {uploadReport.errors?.length > 0 && (
                  <details className="mt-1"><summary className="text-xs cursor-pointer">Ver primeros errores</summary>
                    <pre className="text-xs mt-1 whitespace-pre-wrap">{uploadReport.errors.join('\n')}</pre>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="pgn-upload-submit" onClick={doUpload} disabled={uploading || !selectedDb || !uploadFile}
              className="rounded-lg bg-indigo-600 hover:bg-indigo-700">
              {uploading ? 'Procesando…' : 'Cargar y procesar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModuleNode({ slot, label, items, open, onToggle, onRename, onSelect, selectedId, onDelete, onMove, labels, unassigned }) {
  return (
    <div className="mb-0.5" data-testid={`mod-${slot}`}>
      <div className="group w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 rounded text-sm font-semibold">
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRightIcon className="w-3.5 h-3.5"/>}
          <Folder className="w-4 h-4 text-slate-500"/>
          <span className="truncate">{label}</span>
        </button>
        <span className="text-[10px] text-slate-400 font-mono">{items.length}</span>
        {!unassigned && onRename && (
          <button onClick={onRename} data-testid={`mod-rename-${slot}`}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-200 rounded">
            <Pencil className="w-3 h-3 text-slate-500"/>
          </button>
        )}
      </div>
      {open && (
        <div className="ml-5 mt-0.5 space-y-0.5">
          {items.map(d => (
            <div key={d.id}
              onClick={()=>onSelect(d)}
              data-testid={`db-item-${d.id}`}
              className={`group flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${
                selectedId===d.id ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'hover:bg-slate-100'
              }`}>
              <Lock className="w-3.5 h-3.5"/>
              <span className="flex-1 truncate">{d.name}</span>
              <span className="text-[10px] text-slate-400 font-mono">{d.games_count ?? 0}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button onClick={(e)=>e.stopPropagation()} data-testid={`db-menu-${d.id}`}
                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-slate-200 rounded">
                    <MoreVertical className="w-3.5 h-3.5"/>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled className="text-xs uppercase tracking-widest text-slate-400">Mover a</DropdownMenuItem>
                  {labels.map((n, i) => (
                    <DropdownMenuItem key={i} data-testid={`db-move-${d.id}-${i}`}
                      onClick={(e)=>{ e.stopPropagation(); onMove(d, i); }}>
                      <FolderInput className="w-3.5 h-3.5 mr-2"/> {n}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem data-testid={`db-unassign-${d.id}`}
                    onClick={(e)=>{ e.stopPropagation(); onMove(d, null); }}>
                    <FolderInput className="w-3.5 h-3.5 mr-2"/> Sin módulo
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e)=>{e.stopPropagation(); onDelete(d);}} className="text-rose-600">
                    <Trash2 className="w-3.5 h-3.5 mr-2"/> Borrar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
          {items.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">Vacío</div>}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-16 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 mx-auto mb-4 grid place-items-center">
        <Folder className="w-6 h-6 text-slate-400"/>
      </div>
      <div className="text-slate-500 text-sm">No Data</div>
      <div className="text-xs text-slate-400 mt-1">Carga un archivo .pgn para poblar esta base</div>
    </div>
  );
}
